import React, { useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import DashboardsContext from 'contexts/DashboardsContext';
import ReportsContext from 'contexts/ReportsContext';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import Row from 'components/Common/Row';
import Search from 'components/Search';

import { AddTarget } from './useCreateInsightsContent';

const Toggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const List = styled.div`
  margin-bottom: 24px;
  height: 320px;
  overflow-y: auto;
  padding-right: 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  height: 32px;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

// Picker over EXISTING dashboards/reports for an add_to_existing proposal — used
// as the destination fallback when there's no active entity (or the user wants a
// different target). Mirrors SaveInsightsChartModal's existing-item step.
// Building a NEW dashboard/report is the Phase 3 create flow, so it's not here.
const AddToExistingModal = ({
  chartCount,
  isSaving,
  onPick,
  onClose,
}: {
  chartCount: number;
  isSaving: boolean;
  onPick: (target: AddTarget) => void;
  onClose: () => void;
}) => {
  const { allDashboards } = useContext(DashboardsContext);
  const { availableReports } = useContext(ReportsContext);

  const [tab, setTab] = useState<'dashboard' | 'report'>('dashboard');
  const [searchText, setSearchText] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<
    DashboardType | undefined
  >();
  const [selectedReport, setSelectedReport] = useState<
    ReportType | undefined
  >();

  const filteredDashboards = useMemo(() => {
    const lower = searchText.toLowerCase();
    return allDashboards
      .filter((d) => !d.isWallboardSlide && !d.templateId)
      .filter((d) => d.name.toLowerCase().includes(lower));
  }, [allDashboards, searchText]);
  const filteredReports = useMemo(() => {
    const lower = searchText.toLowerCase();
    return availableReports.filter((r) => r.name.toLowerCase().includes(lower));
  }, [availableReports, searchText]);

  const items: (DashboardType | ReportType)[] =
    tab === 'dashboard' ? filteredDashboards : filteredReports;
  const selectedId =
    tab === 'dashboard' ? selectedDashboard?.id : selectedReport?.id;

  const chartLabel = chartCount === 1 ? '1 chart' : `${chartCount} charts`;

  const onAdd = () => {
    if (tab === 'dashboard' && selectedDashboard) {
      onPick({ kind: 'dashboard', entity: selectedDashboard });
    } else if (tab === 'report' && selectedReport) {
      onPick({ kind: 'report', entity: selectedReport });
    }
  };

  return (
    <Modal onClose={onClose} shouldScrollInViewport={false} autoFocus={false}>
      <Form data-testid="add-to-existing-flow">
        <FormHeader title={`Add ${chartLabel} to`} onClose={onClose} />
        <FormContent>
          <Toggle>
            <Button
              size="Small"
              type={tab === 'dashboard' ? 'Primary' : 'Ghost'}
              label="Dashboard"
              onClick={() => setTab('dashboard')}
              testId="add-to-existing-tab-dashboard"
            />
            <Button
              size="Small"
              type={tab === 'report' ? 'Primary' : 'Ghost'}
              label="Report"
              onClick={() => setTab('report')}
              testId="add-to-existing-tab-report"
            />
          </Toggle>
          <div style={{ marginBottom: 16 }}>
            <Search
              noBorder
              onSearchTextChanged={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchText(e.target.value)
              }
              searchText={searchText}
              onClearClicked={() => setSearchText('')}
            />
          </div>
          <List>
            {items.map((item) => (
              <Item
                key={item.id}
                onClick={() => {
                  if (tab === 'dashboard') {
                    setSelectedDashboard(
                      selectedDashboard?.id === item.id
                        ? undefined
                        : (item as DashboardType),
                    );
                  } else {
                    setSelectedReport(
                      selectedReport?.id === item.id
                        ? undefined
                        : (item as ReportType),
                    );
                  }
                }}
              >
                <Row
                  centerAlign
                  style={{ justifyContent: 'space-between', width: '100%' }}
                >
                  <Typography.Body type="Body 12">{item.name}</Typography.Body>
                  {selectedId === item.id && <Icon icon="checkmark" />}
                </Row>
              </Item>
            ))}
          </List>
        </FormContent>
        <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
          <Button
            onClick={onClose}
            isDisabled={isSaving}
            label="Cancel"
            size="Small"
            type="Ghost"
          />
          <Button
            type="Primary"
            size="Small"
            label="Add"
            onClick={onAdd}
            isDisabled={isSaving || !selectedId}
            isLoading={isSaving}
            testId="add-to-existing-confirm"
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default AddToExistingModal;
